// خدمة التعامل مع ملف CSV الحقيقي للشكاوى
import Papa from 'papaparse';
import {
  determineSector,
  determineMainCategory,
  determineSubCategory,
  getPriorityLevel,
  assignResponsibleEmployee,
  determineInitialStatus,
  isViolenceRelated
} from './agentHelpers.js';

const CSV_FILE_PATH = '/data/complaints_raw_expanded.csv';
let complaintsCache = [];
let complaintCounter = 6200; // العداد للشكاوى الجديدة (بعد 1202 الموجودة)
let usersCache = [];
let feedbackCache = [];

// تحميل بيانات المستخدمين
const loadUsersData = async () => {
  if (usersCache.length === 0) {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL || ''}/data/users_raw_fixed.csv`);
      const csvText = await response.text();
      const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true });
      usersCache = parsed.data.filter(row => row.user_id && row.user_id.trim() !== '');
      console.log('✅ تم تحميل بيانات المستخدمين:', usersCache.length);
    } catch (error) {
      console.error('❌ خطأ في تحميل بيانات المستخدمين:', error);
    }
  }
};

// تحميل بيانات القطاعات والخدمات
let sectorsCache = [];
const loadSectorsData = async () => {
  if (sectorsCache.length === 0) {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL || ''}/data/sectors_services.csv`);
      const csvText = await response.text();
      const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true });
      sectorsCache = parsed.data.filter(row => row.sector && row.sector.trim() !== '');
      console.log('✅ تم تحميل بيانات القطاعات:', sectorsCache.length);
    } catch (error) {
      console.error('❌ خطأ في تحميل بيانات القطاعات:', error);
    }
  }
};

// البحث عن مستخدم بالاسم أو الهاتف أو الإيميل
const findUserByInfo = (name, phone, email) => {
  return usersCache.find(user => 
    (name && user.full_name && user.full_name.includes(name)) ||
    (phone && user.phone === phone) ||
    (email && user.email === email)
  );
};

// التأكد من تحميل الكاش قبل أي عمليات تعتمد عليه
const ensureComplaintsCacheLoaded = async () => {
  if (!complaintsCache || complaintsCache.length === 0) {
    console.log('🧠 الكاش فارغ، سيتم إعادة تحميل البيانات...');
    await loadComplaintsFromCSV();
  }
  await loadUsersData();
  await loadSectorsData();
};

// قراءة الشكاوى من ملف CSV
export const loadComplaintsFromCSV = async () => {
  try {
    console.log('📂 قراءة الشكاوى من ملف CSV...');
    
    // قراءة الملف الأساسي من مجلد public دائماً كخط أساس
    const response = await fetch(`${process.env.PUBLIC_URL || ''}/data/complaints_raw_expanded.csv`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const baseCsvText = await response.text();
    
    const parseCsv = (text) => Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim()
    });
    
    const baseParsed = parseCsv(baseCsvText);
    if (baseParsed.errors.length > 0) {
      console.error('❌ أخطاء في قراءة CSV الأساسي:', baseParsed.errors);
    }
    const baseData = baseParsed.data.filter(row => row.complaint_id && row.complaint_id.trim() !== '');
    
    // محاولة تحميل النسخة المحدثة (إن وُجدت) ثم دمجها مع البيانات الأساسية
    let mergedMap = new Map();
    const addToMap = (list, sourceLabel) => {
      list.forEach(row => {
        if (!row.ticket_id) return;
        const normalized = {
          complaint_id: parseInt(row.complaint_id) || 0,
          ticket_id: row.ticket_id || '',
          citizen_name: row.citizen_name || '',
          phone: row.phone || '',
          email: row.email || '',
          sector: row.sector || '',
          main_category: row.main_category || '',
          sub_category: row.sub_category || '',
          complaint_text: row.complaint_text || '',
          region: row.region || '',
          city: row.city || '',
          status: row.status || 'جديد',
          priority_level: row.priority_level || 'منخفض',
          is_violence_related: row.is_violence_related || 'لا',
          responsible_employee: row.responsible_employee || '',
          channel: row.channel || '',
          created_at: row.created_at || new Date().toISOString()
        };
        const existing = mergedMap.get(normalized.ticket_id);
        if (!existing || new Date(normalized.created_at) > new Date(existing.created_at)) {
          mergedMap.set(normalized.ticket_id, normalized);
        }
      });
      console.log(`📥 تم دمج ${list.length} شكوى من ${sourceLabel}`);
    };
    
    addToMap(baseData, 'base CSV');
    let source = 'base CSV فقط';

    try {
      const autoUpdatedCSV = localStorage.getItem('auto_updated_csv');
      if (autoUpdatedCSV) {
        const autoParsed = parseCsv(autoUpdatedCSV);
        if (autoParsed.errors.length > 0) {
          console.error('❌ أخطاء في قراءة نسخة localStorage:', autoParsed.errors);
        }
        const autoData = autoParsed.data.filter(row => row.complaint_id && row.complaint_id.trim() !== '');
        addToMap(autoData, 'localStorage');
        source = 'base + localStorage';
      }
    } catch (storageError) {
      console.warn('⚠️ تعذر قراءة localStorage:', storageError);
    }
    
    complaintsCache = Array.from(mergedMap.values());
    
    if (baseParsed.errors.length > 0) {
      console.error('❌ أخطاء في قراءة CSV:', baseParsed.errors);
    }
    
    // ترتيب حسب التاريخ - الأحدث أولاً
    complaintsCache.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    // تحديث العداد ليبدأ بعد أعلى رقم موجود
    const maxComplaintId = complaintsCache.reduce((max, complaint) => Math.max(max, complaint.complaint_id || 0), 0);
    complaintCounter = maxComplaintId + 1;
    console.log('🧮 تم تحديث عداد الشكاوى إلى:', complaintCounter);
    
    console.log(`✅ تم تحميل ${complaintsCache.length} شكوى من CSV (المصدر: ${source})`);
    console.log('📋 أول شكوى:', complaintsCache[0]);
    return complaintsCache;
    
  } catch (error) {
    console.error('❌ خطأ في قراءة ملف CSV:', error);
    
    // Fallback للبيانات المحلية
    complaintsCache = getDefaultComplaints();
    console.log(`🔄 استخدام البيانات الافتراضية: ${complaintsCache.length} شكوى`);
    return complaintsCache;
  }
};

// حفظ الشكاوى في ملف CSV (حفظ حقيقي)
export const saveComplaintsToCSV = async (complaints) => {
  try {
    console.log('💾 حفظ الشكاوى في ملف CSV...');
    
    // تحويل البيانات إلى CSV بنفس بنية complaints_raw_expanded.csv
    const csv = Papa.unparse(complaints, {
      header: true,
      columns: [
        'complaint_id', 'user_id', 'sector', 'main_category', 'sub_category', 
        'complaint_text', 'region', 'city', 'email', 'phone', 
        'attachments_count', 'attachments_types', 'status', 'created_at', 'responsible_employee'
      ]
    });
    
    // محاولة حفظ الملف (في بيئة التطوير سنحاكي ونحدث الكاش)
    try {
      // تحديث الكاش المحلي
      complaintsCache = [...complaints];
      
      // إنشاء ملف CSV محدث (بدون تحميل تلقائي)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      
      console.log('📁 تم إنشاء ملف CSV محدث في الذاكرة');
      console.log('📋 محتوى CSV الجديد:', csv.substring(0, 300) + '...');
      
      // حفظ الملف للتحميل الاختياري لاحقاً
      window.csvDownloadData = {
        blob: blob,
        filename: 'complaints_raw_expanded_updated.csv'
      };
      
      // محاولة كتابة في ملف public أيضاً (للمحاكاة)
      try {
        // في بيئة حقيقية، سيتم إرسال للخادم
        localStorage.setItem('live_complaints_csv', csv);
        console.log('💾 تم حفظ نسخة في localStorage أيضاً');
      } catch (storageError) {
        console.warn('⚠️ لا يمكن الحفظ في localStorage');
      }
      
    } catch (fileError) {
      console.warn('⚠️ لا يمكن كتابة الملف مباشرة، تم التحديث في الذاكرة فقط');
    }
    
    console.log('✅ تم حفظ الشكاوى في CSV');
    console.log('📊 إجمالي الشكاوى:', complaints.length);
    console.log('📋 آخر شكوى مضافة:', complaints[0]);
    
    // إشعار تحديث لوحة الموظفين
    notifyDashboardUpdate();
    
    return true;
    
  } catch (error) {
    console.error('❌ خطأ في حفظ ملف CSV:', error);
    return false;
  }
};

// إضافة شكوى جديدة من الشات بوت
export const addComplaintFromChatbot = async (complaintData) => {
  try {
    console.log('📋 إضافة شكوى جديدة من الشات بوت:', complaintData);
    await ensureComplaintsCacheLoaded();
    
    // البحث عن المستخدم في قاعدة البيانات
    const foundUser = findUserByInfo(
      complaintData.citizenData?.name,
      complaintData.citizenData?.phone,
      complaintData.citizenData?.email
    );
    
    console.log('🔍 البحث عن المستخدم:', {
      بحث: complaintData.citizenData?.name,
      نتيجة: foundUser ? foundUser.full_name : 'غير موجود'
    });
    
    // إنشاء الشكوى بنفس بنية complaints_raw_expanded.csv
    const newComplaint = {
      complaint_id: complaintCounter++,
      user_id: foundUser ? foundUser.user_id : Math.floor(Math.random() * 9000) + 1000, // user_id بدلاً من ticket_id
      sector: determineSector(complaintData.agentType),
      main_category: determineMainCategory(complaintData.agentType),
      sub_category: determineSubCategory(complaintData.agentType, complaintData.message),
      complaint_text: complaintData.message,
      region: foundUser ? foundUser.region : (complaintData.citizenData?.region || 'غير محدد'),
      city: foundUser ? foundUser.city : (complaintData.citizenData?.city || 'غير محدد'),
      email: foundUser ? foundUser.email : (complaintData.citizenData?.email || 'غير محدد'),
      phone: foundUser ? foundUser.phone : (complaintData.citizenData?.phone || 'غير محدد'),
      attachments_count: 0, // إضافة الحقول المفقودة
      attachments_types: '',
      status: determineInitialStatus(complaintData.agentType), // حالة ذكية بناءً على نوع الشكوى
      created_at: new Date().toISOString().split('T')[0] + ' ' + new Date().toTimeString().split(' ')[0],
      responsible_employee: assignResponsibleEmployee(complaintData.agentType)
    };
    
    console.log('🤖 تحليل ذكي للشكوى:', {
      'القطاع': newComplaint.sector,
      'الفئة الرئيسية': newComplaint.main_category,
      'الفئة الفرعية': newComplaint.sub_category,
      'الحالة': newComplaint.status,
      'الموظف المسؤول': newComplaint.responsible_employee,
      'متعلق بالعنف': isViolenceRelated(complaintData.agentType, complaintData.message)
    });
    
    // إضافة للكاش في المقدمة (الأحدث أولاً)
    complaintsCache.unshift(newComplaint);
    
    console.log('📋 الشكوى الجديدة:', {
      id: newComplaint.complaint_id,
      ticket: newComplaint.ticket_id,
      citizen: newComplaint.citizen_name,
      type: newComplaint.main_category
    });
    
    // التأكد من تحميل الكاش قبل حفظ الشكوى الجديدة
    await ensureComplaintsCacheLoaded();
    
    // حفظ في ملف CSV
    console.log('💾 بدء حفظ في CSV...');
    await saveComplaintsToCSV(complaintsCache);

    // حفظ تلقائي في ملف CSV الحقيقي
    await updateRealCSVFile(newComplaint);
    
    console.log('✅ تم إضافة الشكوى:', newComplaint.complaint_id);
    console.log('📊 العدد الجديد:', complaintsCache.length);
    console.log('📋 قائمة الشكاوى الحالية:', complaintsCache.map(c => c.ticket_id));
    
    return newComplaint;
    
  } catch (error) {
    console.error('❌ خطأ في إضافة الشكوى:', error);
    throw error;
  }
};

// الحصول على جميع الشكاوى (مرتبة بالأحدث أولاً)
export const getAllComplaints = () => {
  return complaintsCache.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
};

// الحصول على إحصائيات الشكاوى
export const getComplaintsStats = () => {
  const total = complaintsCache.length;
  
  const byStatus = complaintsCache.reduce((acc, complaint) => {
    acc[complaint.status] = (acc[complaint.status] || 0) + 1;
    return acc;
  }, {});
  
  const byPriority = complaintsCache.reduce((acc, complaint) => {
    acc[complaint.priority_level] = (acc[complaint.priority_level] || 0) + 1;
    return acc;
  }, {});
  
  return {
    total,
    newTickets: byStatus['جديد'] || 0,
    inProgress: byStatus['قيد المعالجة'] || 0,
    resolved: byStatus['مكتمل'] || 0,
    urgent: byPriority['طارئ'] || 0,
    byStatus,
    byPriority
  };
};

// تحديث حالة الشكوى
export const updateComplaintStatus = async (complaintId, newStatus) => {
  const complaint = complaintsCache.find(c => c.complaint_id === complaintId);
  if (complaint) {
    complaint.status = newStatus;
    
    // حفظ التحديث في CSV
    await saveComplaintsToCSV(complaintsCache);
    
    console.log(`✅ تم تحديث حالة الشكوى ${complaintId} إلى: ${newStatus}`);
    return complaint;
  }
  return null;
};

// تعيين الموظف للشكوى
export const assignComplaintToEmployee = async (complaintId, employeeName) => {
  const complaint = complaintsCache.find(c => c.complaint_id === complaintId);
  if (complaint) {
    complaint.responsible_employee = employeeName;
    
    // حفظ التحديث في CSV
    await saveComplaintsToCSV(complaintsCache);
    
    console.log(`✅ تم تعيين الشكوى ${complaintId} للموظف: ${employeeName}`);
    return complaint;
  }
  return null;
};

// البيانات الافتراضية (Fallback)
const getDefaultComplaints = () => [
  {
    complaint_id: 5001,
    ticket_id: 'CASE-2024-001',
    citizen_name: 'أحمد محمد الزهراني',
    phone: '0501234567',
    email: 'ahmed@example.com',
    sector: 'العمل',
    main_category: 'حساب الخدمات الإلكترونية',
    sub_category: 'استعادة كلمة المرور',
    complaint_text: 'مشكلة في استعادة كلمة المرور للحساب الإلكتروني',
    region: 'المدينة المنورة',
    city: 'الحناكية',
    status: 'جديد',
    priority_level: 'متوسط',
    is_violence_related: 'لا',
    responsible_employee: 'سارة القحطاني',
    channel: 'منصة إلكترونية',
    created_at: '2024-11-14T06:30:00Z'
  },
  {
    complaint_id: 5002,
    ticket_id: 'CASE-2024-002',
    citizen_name: 'فاطمة أحمد العنزي',
    phone: '0501234568',
    email: 'fatima@example.com',
    sector: 'التنمية',
    main_category: 'الحماية الاجتماعية',
    sub_category: 'عنف أسري',
    complaint_text: 'العنف زاد والوضع أسوأ من قبل، أحتاج مساعدة عاجلة',
    region: 'المنطقة الشرقية',
    city: 'الدمام',
    status: 'جديد',
    priority_level: 'طارئ',
    is_violence_related: 'نعم',
    responsible_employee: 'منى السعد',
    channel: 'شات بوت',
    created_at: '2024-11-14T07:13:21Z'
  },
  {
    complaint_id: 5003,
    ticket_id: 'CASE-2024-003',
    citizen_name: 'خالد عبدالله المطيري',
    phone: '0501234569',
    email: 'khalid@example.com',
    sector: 'العمل',
    main_category: 'حقوق العمال',
    sub_category: 'تأخير الرواتب',
    complaint_text: 'راتبي متأخر شهرين والشركة ترفض دفعه',
    region: 'نجران',
    city: 'نجران',
    status: 'قيد المعالجة',
    priority_level: 'عالي',
    is_violence_related: 'لا',
    responsible_employee: 'أحمد الزهراني',
    channel: 'هاتف',
    created_at: '2024-11-13T14:20:00Z'
  },
  {
    complaint_id: 5004,
    ticket_id: 'CASE-2024-004',
    citizen_name: 'نورا سعد الغامدي',
    phone: '0501234570',
    email: 'nora@example.com',
    sector: 'التنمية',
    main_category: 'التأهيل الاجتماعي',
    sub_category: 'خدمة تقييم الإعاقة',
    complaint_text: 'طلب تقييم إعاقة لابني معلق منذ شهر',
    region: 'المنطقة الشرقية',
    city: 'الظهران',
    status: 'مكتمل',
    priority_level: 'متوسط',
    is_violence_related: 'لا',
    responsible_employee: 'سلمى الأحمد',
    channel: 'زيارة مكتب',
    created_at: '2024-11-12T09:45:00Z'
  },
  {
    complaint_id: 5005,
    ticket_id: 'CASE-2024-005',
    citizen_name: 'محمد علي السلمي',
    phone: '0501234571',
    email: 'mohammed@example.com',
    sector: 'العمل',
    main_category: 'التطبيق الموحد HRSD',
    sub_category: 'عدم القدرة على تسجيل الدخول',
    complaint_text: 'لا أستطيع تسجيل الدخول على التطبيق منذ أسبوع',
    region: 'الرياض',
    city: 'الرياض',
    status: 'جديد',
    priority_level: 'متوسط',
    is_violence_related: 'لا',
    responsible_employee: 'سارة القحطاني',
    channel: 'شات بوت',
    created_at: '2024-11-14T05:20:00Z'
  },
  {
    complaint_id: 5006,
    ticket_id: 'CASE-2024-006',
    citizen_name: 'سارة أحمد الحربي',
    phone: '0501234572',
    email: 'sara@example.com',
    sector: 'التنمية',
    main_category: 'الضمان الاجتماعي',
    sub_category: 'تأخير صرف المساعدة',
    complaint_text: 'تأخر صرف الضمان الاجتماعي لهذا الشهر',
    region: 'المنطقة الغربية',
    city: 'جدة',
    status: 'قيد المعالجة',
    priority_level: 'عالي',
    is_violence_related: 'لا',
    responsible_employee: 'منى السعد',
    channel: 'منصة إلكترونية',
    created_at: '2024-11-13T16:45:00Z'
  },
  {
    complaint_id: 5007,
    ticket_id: 'CASE-2024-007',
    citizen_name: 'عبدالله محمد القحطاني',
    phone: '0501234573',
    email: 'abdullah@example.com',
    sector: 'العمل',
    main_category: 'تسجيل المنشأة',
    sub_category: 'مشكلة في تسجيل منشأة جديدة',
    complaint_text: 'واجهت صعوبة في تسجيل منشأتي الجديدة',
    region: 'عسير',
    city: 'أبها',
    status: 'جديد',
    priority_level: 'متوسط',
    is_violence_related: 'لا',
    responsible_employee: 'أحمد الزهراني',
    channel: 'هاتف',
    created_at: '2024-11-14T04:15:00Z'
  },
  {
    complaint_id: 5008,
    ticket_id: 'CASE-2024-008',
    citizen_name: 'هند سعد العتيبي',
    phone: '0501234574',
    email: 'hind@example.com',
    sector: 'التنمية',
    main_category: 'رعاية الأيتام',
    sub_category: 'استعلام عن برنامج كافل',
    complaint_text: 'أريد الاستعلام عن شروط برنامج كافل',
    region: 'الرياض',
    city: 'الدرعية',
    status: 'مكتمل',
    priority_level: 'منخفض',
    is_violence_related: 'لا',
    responsible_employee: 'سلمى الأحمد',
    channel: 'زيارة مكتب',
    created_at: '2024-11-11T11:30:00Z'
  },
  {
    complaint_id: 5009,
    ticket_id: 'CASE-2024-009',
    citizen_name: 'يوسف أحمد الشهري',
    phone: '0501234575',
    email: 'yousef@example.com',
    sector: 'العمل',
    main_category: 'حقوق العمال',
    sub_category: 'انتهاك حقوق العامل',
    complaint_text: 'الشركة لا تلتزم بساعات العمل المحددة',
    region: 'الباحة',
    city: 'الباحة',
    status: 'جديد',
    priority_level: 'عالي',
    is_violence_related: 'لا',
    responsible_employee: 'أحمد الزهراني',
    channel: 'شات بوت',
    created_at: '2024-11-14T03:45:00Z'
  },
  {
    complaint_id: 5010,
    ticket_id: 'CASE-2024-010',
    citizen_name: 'أمل محمد الدوسري',
    phone: '0501234576',
    email: 'amal@example.com',
    sector: 'التنمية',
    main_category: 'التأهيل المهني',
    sub_category: 'طلب دورة تدريبية',
    complaint_text: 'أريد التسجيل في دورة تدريبية للخياطة',
    region: 'الرياض',
    city: 'الخرج',
    status: 'قيد المعالجة',
    priority_level: 'متوسط',
    is_violence_related: 'لا',
    responsible_employee: 'سلمى الأحمد',
    channel: 'منصة إلكترونية',
    created_at: '2024-11-13T12:20:00Z'
  }
];

// إشعار تحديث لوحة الموظفين
const notifyDashboardUpdate = () => {
  console.log('🔔 تحديث لوحة الموظفين - بيانات جديدة من CSV');
  
  // إرسال حدث مخصص للتحديث
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('complaintsUpdated', {
      detail: { 
        timestamp: new Date().toISOString(),
        source: 'CSV',
        total: complaintsCache.length
      }
    }));
  }
};

// تحديث ملف CSV الحقيقي تلقائياً
const updateRealCSVFile = async (newComplaint) => {
  try {
    console.log('📁 تحديث ملف CSV الحقيقي تلقائياً...');
    
    // قراءة الملف الحالي
    const currentComplaints = [...complaintsCache];
    
    // تحويل إلى CSV
    const csvContent = Papa.unparse(currentComplaints, {
      header: true,
      columns: [
        'complaint_id', 'ticket_id', 'citizen_name', 'phone', 'email',
        'sector', 'main_category', 'sub_category', 'complaint_text',
        'region', 'city', 'status', 'priority_level', 'is_violence_related',
        'responsible_employee', 'channel', 'created_at'
      ]
    });
    
    // حفظ في localStorage للمحاكاة
    localStorage.setItem('auto_updated_csv', csvContent);
    localStorage.setItem('csv_last_updated', new Date().toISOString());
    
    console.log('✅ تم تحديث ملف CSV تلقائياً');
    console.log('📋 الشكوى الجديدة المضافة:', newComplaint.ticket_id);
    
    return true;
  } catch (error) {
    console.error('❌ خطأ في تحديث ملف CSV:', error);
    return false;
  }
};

// إعادة تحميل البيانات يدوياً
export const refreshComplaintsData = async () => {
  console.log('🔄 إعادة تحميل بيانات الشكاوى...');
  complaintsCache = [];
  await loadComplaintsFromCSV();
  
  // إشعار تحديث لوحة الموظفين
  notifyDashboardUpdate();
  
  console.log(`✅ تم تحديث البيانات - ${complaintsCache.length} شكوى متاحة`);
  return complaintsCache;
};

// تحميل ملف CSV (اختياري)
export const downloadCSVFile = () => {
  try {
    if (window.csvDownloadData) {
      const url = URL.createObjectURL(window.csvDownloadData.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = window.csvDownloadData.filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      console.log('📥 تم تحميل ملف CSV بنجاح');
    } else {
      console.warn('⚠️ لا يوجد ملف CSV متاح للتحميل');
    }
  } catch (error) {
    console.error('❌ خطأ في تحميل ملف CSV:', error);
  }
};

// تهيئة النظام
export const initializeCSVService = async () => {
  console.log('🚀 تهيئة خدمة CSV للشكاوى...');
  
  // إعادة تعيين الكاش لضمان قراءة جديدة
  complaintsCache = [];
  
  try {
    await loadComplaintsFromCSV();
    console.log(`📊 تم تحميل ${complaintsCache.length} شكوى من CSV`);
  } catch (error) {
    console.error('❌ فشل في قراءة CSV:', error);
  }
  
  // إذا لم تنجح قراءة CSV، استخدم البيانات الافتراضية
  if (complaintsCache.length === 0) {
    console.log('🔄 استخدام البيانات الافتراضية...');
    complaintsCache = getDefaultComplaints();
  }
  
  console.log(`✅ تم تهيئة خدمة CSV بنجاح - ${complaintsCache.length} شكوى متاحة`);
  console.log('📋 أول شكوى في الكاش:', complaintsCache[0]);
};

export default {
  loadComplaintsFromCSV,
  saveComplaintsToCSV,
  addComplaintFromChatbot,
  getAllComplaints,
  getComplaintsStats,
  updateComplaintStatus,
  assignComplaintToEmployee,
  initializeCSVService
};
